/**
 * Session Manager for ehr-frontend
 *
 * Ported from va-portal-main's sessionManager.js, adapted for this app.
 *
 * KEY DIFFERENCE FROM va-portal:
 * va-portal owns a real login flow (email/password). This app has NO login of
 * its own — it is launched with a short-lived JWT passed in the URL (see
 * `utils/bootstrap.js`). On a new session we exchange that URL JWT for a
 * long-lived refresh token (`exchangeTokenForRefreshApi`), then exchange the refresh
 * token for a fresh access token (`refreshAccessTokenApi`). Tokens are stored in
 * the Vuex `login` module / sessionStorage (`token`/`exp`/`refreshToken`/
 * `refreshTokenExpiry`, epoch seconds).
 *
 * The silent-refresh machinery from va-portal is retained: the access token is
 * refreshed in the foreground before it expires, and the session ends when the
 * refresh token itself expires. Logout is delegated to the `login/logout` Vuex
 * action (which clears state and redirects back to the parent portal).
 *
 * There is NO cross-tab logout sync: tokens live in per-tab sessionStorage, so
 * each tab is an independent session and sign-out is intentionally per-tab.
 *
 * NOTE: this app deliberately has NO idle / inactivity timeout. The session ends
 * when the refresh token can no longer be renewed — its ~14-day expiry minus a
 * small buffer — enforced by getEffectiveSessionEnd(). That refresh-token ceiling
 * IS the absolute limit; we don't track our own in production. (A DEMO-only cap
 * from session start can force a faster timeout for demos/tests.) Backgrounded
 * tabs stay alive until that ceiling.
 *
 * Features kept from the original:
 * - Silent access-token refresh via a long-lived refresh token
 * - Absolute session limit (14 days) / refresh-token expiry ceiling
 * - A warning shown shortly before expiry
 * - Graceful, silent error handling
 */

import { Notification } from '@vibrant-wellness/va-responsive-components-library';
import store from '../store';
import { isEmbedMode } from './embedMode';
import { exchangeTokenForRefreshApi, refreshAccessTokenApi } from '../api/auth';

// Decode the `exp` (epoch seconds) claim from a JWT access token. Used as a
// fallback when the refresh response omits the expiry. Returns undefined if the
// token is not a decodable JWT (e.g. an opaque token).
function decodeJwtExp(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp : undefined;
  } catch {
    return undefined;
  }
}

// The token endpoints return expiries as epoch-seconds strings ("1750620900").
// Normalize to an integer; return undefined when absent so updateTokens' guards
// (`if (x !== undefined)`) preserve the existing value rather than clobbering it.
function toEpochSeconds(value) {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const n = typeof value === 'number' ? value : parseInt(value, 10);
  return Number.isFinite(n) ? n : undefined;
}

// ============================================================================
// ⚠️  PRE-LAUNCH: set BOTH switches below to false and verify before shipping.
// They are the ONLY demo/test knobs in this file — everything else derives from
// SESSION_TESTING.
//
//   SESSION_TESTING            — master switch. Compresses all session timers,
//                                enables the demo absolute cap, and turns on the
//                                verbose per-API-call refresh logging.
//   FORCE_REFRESH_ON_EVERY_CALL — forces a token refresh on every API call so the
//                                refresh can be observed live (independent toggle).
// ============================================================================
const SESSION_TESTING = false;
const FORCE_REFRESH_ON_EVERY_CALL = false;

// Session timeout constants (in milliseconds): test value : production value.
const ABSOLUTE_WARNING_BEFORE_EXPIRY = SESSION_TESTING ? 10 * 1000 : 10 * 60 * 1000; // 10s : 10 min
const SESSION_CHECK_INTERVAL         = SESSION_TESTING ? 5 * 1000  : 60 * 1000;       // 5s : 1 min
const REFRESH_TOKEN_EXPIRY_THRESHOLD = SESSION_TESTING ? 10 * 1000 : 30 * 60 * 1000;  // 10s : 30 min
const ACCESS_TOKEN_REFRESH_WINDOW    = SESSION_TESTING ? 10 * 1000 : 5 * 60 * 1000;   // 10s : 5 min

// DEMO-only absolute cap measured from session start, to force a fast timeout
// from login. PRODUCTION (null): the refresh token already carries the ~14-day
// expiry, so the refresh-token ceiling IS the absolute limit — we don't track our own.
const DEMO_ABSOLUTE_SESSION_LIMIT = SESSION_TESTING ? 40 * 1000 : null; // 40s : disabled

// Verbose logging for the on-demand refresh path (fires per API call) — test only.
const debugLog = (...args) => {
  if (SESSION_TESTING) {
    console.log(...args);
  }
};

// sessionStorage keys (per-tab — same store as the auth tokens, so the absolute
// session clock is scoped to the tab, not shared across tabs).
const STORAGE_KEYS = {
  SESSION_START: 'sessionStartTime',
};

// After a failed on-demand refresh, pause further on-demand refresh attempts for
// this long so a failing /utility/refreshToken isn't hammered on every API call
// (the forced refresh path bypasses refreshToken's own throttle). Not a session
// timeline value, so it is NOT compressed for testing.
const REFRESH_FAILURE_COOLDOWN = 5 * 1000;

// Process-wide refresh coordination — module-level so it spans EVERY refresh
// entry point (ensureFreshToken, bootstrap, init), not just one caller:
//  - inFlightRefresh: the single in-flight refresh promise, or null. Concurrent
//    callers share it so we never fire parallel refreshes (which, with refresh-
//    token rotation, would 401 all but one).
//  - lastRefreshFailureAt: epoch ms of the last failed refresh, for the backoff.
let inFlightRefresh = null;
let lastRefreshFailureAt = 0;

class SessionManager {
  constructor() {
    this.isActive = false;
    this.sessionCheckInterval = null;
    this.lastTokenRefresh = 0;
    this.absoluteTimeoutWarningShown = false;
  }

  /**
   * Initialize the session manager.
   * Call this once a token has been established (after `bootstrapAuth`).
   * @returns {Promise<boolean>} - true if a valid session is active, false otherwise.
   */
  async initialize() {
    if (this.isActive) {
      return true;
    }

    // No token means there is nothing to manage (token comes from the URL).
    if (!this.getTokenExpiry() && !store.getters['login/isAuthed']) {
      return false;
    }

    const existingSessionStart = sessionStorage.getItem(STORAGE_KEYS.SESSION_START);

    if (!existingSessionStart) {
      this.startNewSession();
    } else {
      const expiredReason = this.getExistingSessionExpiry();
      if (expiredReason) {
        this.handleSessionExpiry(expiredReason);
        return false;
      }
    }

    // Verify a fresh access token is in place before any downstream callers
    // proceed. isActive is set first so refreshToken()'s guard allows it.
    // Ongoing access-token freshness is handled on-demand per API call via
    // ensureFreshToken(); there is no proactive refresh timer.
    this.isActive = true;
    const tokensValid = await this.checkAndRefreshTokenIfExpired();
    if (!tokensValid) {
      // The session has no usable access token. On the 401 path
      // handleSessionExpiry already ran (which resets isActive); but other false
      // returns (e.g. fallback mode with an expired access token) do NOT log out,
      // so reset state here too. Otherwise initialize() would return false while
      // leaving isActive=true and never starting startSessionChecks() — a stuck,
      // half-initialized session. cleanup() is idempotent, so this is safe even
      // when handleSessionExpiry already cleaned up.
      this.cleanup();
      return false;
    }

    this.absoluteTimeoutWarningShown = false;
    this.startSessionChecks();

    return true;
  }

  /**
   * Bootstrap a brand-new session created from a URL JWT.
   *
   * Per the auth model: first exchange the URL JWT for a refresh token, then
   * exchange the refresh token for a fresh access token. Both tokens are stored
   * in the login module. Call this from bootstrapAuth BEFORE initialize().
   *
   * @param {string} urlToken - the JWT that arrived in the URL.
   * @returns {Promise<boolean>} - true if a valid access token was obtained.
   */
  async bootstrapNewSession(urlToken) {
    if (!urlToken) {
      return false;
    }

    // A fresh URL token means a brand-new session. Reset the absolute-timeout
    // clock NOW so a prior session's SESSION_START still in THIS tab's
    // sessionStorage (e.g. a same-tab re-launch, or a duplicated tab that copied
    // sessionStorage) doesn't make initialize() treat this as an already-expired
    // session and log out instantly.
    this.startNewSession();

    // STEP 1: exchange the URL JWT for a long-lived refresh token.
    const gotRefreshToken = await this.fetchRefreshToken(urlToken);
    if (!gotRefreshToken) {
      return false;
    }

    // STEP 2: exchange the refresh token for a fresh access token. suppressLogout
    // because bootstrapAuth owns the fallback (it uses the URL access token on a
    // false return) — letting this trigger handleSessionExpiry would race with
    // that fallback and flash a spurious "Session Expired" toast on load.
    const result = await this.sharedRefresh({ suppressLogout: true });
    if (!result || !result.success) {
      if (result && result.error === 'unauthorized') {
        // The refresh token we just obtained was rejected (not a transient
        // network/5xx error). Drop it so we fall back cleanly to URL-token-only
        // mode instead of retrying a known-bad token later in the session.
        store.dispatch('login/updateTokens', { refreshToken: '', refreshTokenExpiry: 0 });
      }
      return false;
    }
    return true;
  }

  /**
   * STEP 1 of the URL-token flow: exchange the URL JWT for a long-lived refresh
   * token and persist it (with its expiry). This establishes ONLY the refresh
   * token — the access token is minted in step 2 (refreshToken) by exchanging
   * this refresh token, so the access token and its expiry come from a single
   * canonical source rather than this endpoint's incidental response. The URL
   * JWT set by bootstrapAuth remains the interim access token until step 2 runs.
   * @param {string} accessToken - the JWT that arrived in the URL.
   * @returns {Promise<boolean>} - true if a refresh token was obtained.
   */
  async fetchRefreshToken(accessToken) {
    try {
      const response = await exchangeTokenForRefreshApi(accessToken);
      const data = (response && response.data) || {};
      const refreshToken = data.refresh_token;

      if (!refreshToken) {
        return false;
      }

      const refreshTokenExpiry = toEpochSeconds(data.refresh_token_expiry);

      store.dispatch('login/updateTokens', {
        refreshToken,
        refreshTokenExpiry,
      });

      console.log('[SessionManager] Refresh token obtained from URL token', {
        refreshTokenExpiry: refreshTokenExpiry
          ? new Date(refreshTokenExpiry * 1000).toISOString()
          : null,
      });

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Read the access-token expiry (in ms) from the login store / sessionStorage.
   * The token is a JWT whose `exp` (epoch seconds) is persisted by bootstrapAuth.
   * @returns {number|null} expiry timestamp in milliseconds, or null if unknown.
   */
  getTokenExpiry() {
    const exp =
      store.state?.login?.exp ||
      parseInt(sessionStorage.getItem('exp') || '0', 10);
    return exp ? exp * 1000 : null;
  }

  /**
   * Read the refresh-token expiry (in ms) from the login store / sessionStorage.
   * @returns {number|null} expiry timestamp in milliseconds, or null if unknown.
   */
  getRefreshTokenExpiry() {
    const exp =
      store.state?.login?.refreshTokenExpiry ||
      parseInt(sessionStorage.getItem('refreshTokenExpiry') || '0', 10);
    return exp ? exp * 1000 : null;
  }

  /**
   * @returns {string} the stored refresh token, or '' if none.
   */
  getRefreshToken() {
    return store.state?.login?.refreshToken || sessionStorage.getItem('refreshToken') || '';
  }

  /**
   * @returns {boolean} true if the access-token expiry is known and in the past.
   */
  isTokenExpired() {
    const expiry = this.getTokenExpiry();
    if (!expiry) {
      return false; // Unknown expiry — don't force a logout.
    }
    return Date.now() >= expiry;
  }

  /**
   * @returns {boolean} true if the refresh token is missing or (nearly) expired.
   */
  isRefreshTokenExpired() {
    if (!this.getRefreshToken()) {
      return true;
    }
    const expiry = this.getRefreshTokenExpiry();
    if (!expiry) {
      return false; // Unknown expiry — assume still valid.
    }
    return Date.now() >= expiry - REFRESH_TOKEN_EXPIRY_THRESHOLD;
  }

  /**
   * The single, authoritative session-end timeline.
   *
   * PRODUCTION: the session ends at the token ceiling — the refresh token's expiry
   * minus REFRESH_TOKEN_EXPIRY_THRESHOLD (we end early by the buffer so we never
   * run past a refresh we can no longer make). The refresh token already carries
   * the ~14-day cap, so this IS the absolute session limit. In fallback mode (no
   * refresh token) the access token's own expiry is the ceiling instead.
   *
   * DEMO/TEST: if DEMO_ABSOLUTE_SESSION_LIMIT is set, also cap at
   * sessionStart + that value and take whichever end is EARLIER, so demos can
   * force a fast timeout. Disabled (null) in production.
   *
   * Both the pre-expiry warning and the hard logout key off this single value, so
   * the warning (at end − ABSOLUTE_WARNING_BEFORE_EXPIRY) always precedes logout
   * (at end) regardless of which deadline binds.
   *
   * @returns {{ end: number|null, reason: string|null }} effective end (ms epoch)
   *   and the reason that bound it, or {null, null} if no deadline is known.
   */
  getEffectiveSessionEnd() {
    let end = null;
    let reason = null;

    const consider = (candidate, candidateReason) => {
      if (candidate === null || candidate === undefined) {
        return;
      }
      if (end === null || candidate < end) {
        end = candidate;
        reason = candidateReason;
      }
    };

    // DEMO-only absolute cap from session start. Disabled in production
    // (DEMO_ABSOLUTE_SESSION_LIMIT === null) — the refresh-token ceiling below is
    // the real limit there, so we don't depend on SESSION_START.
    if (DEMO_ABSOLUTE_SESSION_LIMIT != null) {
      const sessionStart = parseInt(sessionStorage.getItem(STORAGE_KEYS.SESSION_START) || '0', 10);
      if (sessionStart) {
        consider(sessionStart + DEMO_ABSOLUTE_SESSION_LIMIT, 'absolute');
      }
    }

    // Token ceiling: buffered refresh-token expiry, or (fallback) access-token expiry.
    if (this.getRefreshToken()) {
      const rtExpiry = this.getRefreshTokenExpiry();
      if (rtExpiry) {
        consider(rtExpiry - REFRESH_TOKEN_EXPIRY_THRESHOLD, 'refresh_token_expired');
      }
    } else {
      const tokenExpiry = this.getTokenExpiry();
      if (tokenExpiry) {
        consider(tokenExpiry, 'absolute');
      }
    }

    return { end, reason };
  }

  /**
   * On init / tab-return: refresh the access token if it is expired or about to
   * expire. This only owns the ACCESS-token refresh; whether the session itself
   * is over is decided by getEffectiveSessionEnd() (checkSessionTimeouts /
   * getExistingSessionExpiry), so callers must run that check first.
   * @returns {Promise<boolean>} - true if the session has a usable access token.
   */
  async checkAndRefreshTokenIfExpired() {
    // Fallback / no-refresh mode: no refresh token was ever issued (e.g. the
    // backend endpoints aren't live yet). Nothing to refresh — the access token
    // is all we have, and the effective-end check owns logout.
    if (!this.getRefreshToken()) {
      return !this.isTokenExpired();
    }

    const expiry = this.getTokenExpiry();
    if (!expiry) {
      return true; // Unknown access-token expiry — assume OK.
    }

    const timeUntilExpiry = expiry - Date.now();
    if (timeUntilExpiry < ACCESS_TOKEN_REFRESH_WINDOW) {
      const result = await this.sharedRefresh(); // Force refresh (shared single-flight).
      if (result && result.success) {
        return true;
      }
      // A genuine auth rejection ('unauthorized') already triggered
      // handleSessionExpiry (logout) inside refreshToken, so report the session as
      // unusable. But a TRANSIENT failure (5xx / network) must NOT abort init: the
      // refresh token is still valid, so proceed — returning false here would skip
      // startSessionChecks() — and let on-demand refresh recover later.
      return !(result && result.error === 'unauthorized');
    }
    return true;
  }

  /**
   * Silently refresh the access token using the stored refresh token.
   * @param {boolean} force - refresh even if the access token is still fresh.
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async refreshToken(force = false, { suppressLogout = false } = {}) {
    // Bail out if the session is inactive, unless this is a forced bootstrap call.
    if (!this.isActive && !force) {
      return { success: false, error: 'session_inactive' };
    }

    const now = Date.now();

    // Skip if the token is still fresh (unless forced).
    if (!force) {
      const expiry = this.getTokenExpiry();
      if (expiry && (expiry - now) > ACCESS_TOKEN_REFRESH_WINDOW) {
        return { success: false, error: 'not_needed' };
      }
      // Avoid hammering the endpoint (min 1 minute between refreshes).
      if (this.lastTokenRefresh && (now - this.lastTokenRefresh) < 60 * 1000) {
        return { success: false, error: 'too_recent' };
      }
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      // No refresh token (fallback mode). Can't refresh — let checkSessionTimeouts
      // handle absolute expiry based on the access token instead of logging out here.
      return { success: false, error: 'no_refresh_token' };
    }

    try {
      const response = await refreshAccessTokenApi(refreshToken);
      const data = (response && response.data) || {};

      // Tolerate both field namings (access_token from the exchange endpoint,
      // token from the legacy refresh endpoint).
      const accessToken = data.access_token ?? data.token;

      if (!accessToken) {
        throw new Error('No token in refresh response');
      }

      // Resolve the new access-token expiry: from the response, else decode it
      // from the JWT, else 0 (unknown). We must NOT pass undefined here — that
      // makes updateTokens KEEP the previous (now-expired) exp, after which the
      // exp-checking request interceptors would clear auth on the very next call
      // despite the fresh bearer token. exp = 0 is treated as "unknown" (those
      // interceptors skip the clear) rather than "expired".
      let accessExpiry = toEpochSeconds(data.access_token_expiry ?? data.token_expiry);
      if (accessExpiry === undefined) {
        accessExpiry = decodeJwtExp(accessToken);
      }

      // Update ONLY the access token (and refresh token if the backend rotated
      // it). RocketChat creds and userInfo are preserved via updateTokens.
      store.dispatch('login/updateTokens', {
        token: accessToken,
        exp: accessExpiry ?? 0,
        refreshToken: data.refresh_token, // undefined-safe inside the mutation
        refreshTokenExpiry: toEpochSeconds(data.refresh_token_expiry),
      });
      this.lastTokenRefresh = now;

      console.log('[SessionManager] Access token refreshed', {
        forced: force,
        tokenExpiry: accessExpiry ? new Date(accessExpiry * 1000).toISOString() : null,
      });

      return { success: true };
    } catch (error) {
      const status = error.response && error.response.status;

      if (status === 400 || status === 401) {
        // Refresh token rejected. Mid-session this ends the session; but when
        // suppressLogout is set (the bootstrap path) the CALLER owns the fallback,
        // so we must NOT trigger a logout/redirect here — doing so would race with
        // bootstrapAuth re-establishing the session from the URL token (and would
        // flash a spurious "Session Expired" toast during initial load).
        if (!suppressLogout) {
          this.handleSessionExpiry('token_refresh_failed');
        }
        return { success: false, error: 'unauthorized' };
      }
      if (status >= 500) {
        // Server error — keep the session, retry on the next tick.
        return { success: false, error: 'server_error' };
      }
      // Network or other transient error — don't force logout.
      return { success: false, error: error.message };
    }
  }

  /**
   * Coalesce all refresh callers onto ONE in-flight network refresh (process-wide
   * single-flight) and record failures for the on-demand backoff. EVERY refresh
   * entry point (ensureFreshToken, bootstrapNewSession, checkAndRefreshTokenIfExpired,
   * the bootstrap reload path) should go through this rather than calling
   * refreshToken() directly, so concurrent callers never fire parallel refreshes.
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  sharedRefresh({ suppressLogout = false } = {}) {
    if (!inFlightRefresh) {
      inFlightRefresh = this.refreshToken(true, { suppressLogout })
        .then((result) => {
          if (!result || !result.success) {
            lastRefreshFailureAt = Date.now();
          }
          return result;
        })
        .catch((error) => {
          lastRefreshFailureAt = Date.now();
          return { success: false, error: error && error.message };
        })
        .finally(() => {
          inFlightRefresh = null;
        });
    }
    return inFlightRefresh;
  }

  /**
   * Start a new session by recording the start time.
   */
  startNewSession() {
    sessionStorage.setItem(STORAGE_KEYS.SESSION_START, Date.now().toString());
    this.absoluteTimeoutWarningShown = false;
  }

  /**
   * Check an existing session on app load against the unified timeline.
   * @returns {string|null} the expiry reason if the session is over, else null.
   */
  getExistingSessionExpiry() {
    const now = Date.now();

    // Absolute / token ceiling — same timeline the periodic check enforces.
    const { end, reason } = this.getEffectiveSessionEnd();
    if (end !== null && now >= end) {
      return reason || 'absolute';
    }

    return null;
  }

  /**
   * Start the periodic absolute / token-expiry timeout check.
   */
  startSessionChecks() {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }
    this.sessionCheckInterval = setInterval(() => {
      this.checkSessionTimeouts();
    }, SESSION_CHECK_INTERVAL);
  }

  /**
   * The periodic owner of the session-end timeline: warn, then log out, both
   * keyed off the single effectiveEnd from getEffectiveSessionEnd(). Because the
   * warning fires at end − ABSOLUTE_WARNING_BEFORE_EXPIRY and logout at end, the
   * warning is guaranteed to precede logout regardless of which deadline binds.
   */
  checkSessionTimeouts() {
    const { end, reason } = this.getEffectiveSessionEnd();

    if (end !== null) {
      const timeUntilEnd = end - Date.now();

      if (timeUntilEnd <= 0) {
        this.handleSessionExpiry(reason || 'absolute');
        return;
      }

      if (!this.absoluteTimeoutWarningShown && timeUntilEnd <= ABSOLUTE_WARNING_BEFORE_EXPIRY) {
        this.absoluteTimeoutWarningShown = true;
        Notification.toast({
          title: 'Session Expiring Soon',
          message: 'Your session will expire in about 10 minutes. Please save your work.',
          theme: 'warning',
          hide_after: 0, // Persistent until dismissed.
        });
      }
    }
  }

  /**
   * Handle session expiry — notify the user and log out.
   * @param {string} reason - 'absolute' | 'refresh_token_expired' | 'token_refresh_failed'.
   */
  handleSessionExpiry(reason) {
    console.log('[SessionManager] Logout (session expired)', { reason: reason || 'unknown' });

    this.cleanup();
    this.clearSessionData();

    let message = 'Your session has expired. Please log in again to continue.';
    if (reason === 'absolute' || reason === 'refresh_token_expired') {
      message = 'Your session has reached its maximum duration. Please log in again to continue.';
    } else if (reason === 'token_refresh_failed') {
      message = 'Your session could not be verified. Please log in again to continue.';
    }

    Notification.toast({
      title: 'Session Expired',
      message,
      theme: 'warning',
      hide_after: 5000,
    });

    this.redirectToLogin();
  }

  /**
   * Clear all auth/session data.
   * Delegates token/user cleanup to the login store, then removes the
   * session-manager bookkeeping keys.
   */
  clearSessionData() {
    try {
      store.dispatch('login/clear');
    } catch (error) {
      // Store may be unavailable in edge cases — ignore.
    }
    sessionStorage.removeItem(STORAGE_KEYS.SESSION_START);
  }

  /**
   * Redirect the user out of the app after expiry/logout.
   *
   * Unlike va-portal, this app has no internal /login route. When standalone,
   * we hand off to the `login/logout` action (which redirects to the parent
   * portal's login page).
   *
   * When embedded inside va-portal this is a NO-OP: va-portal owns auth and will
   * reload the iframe with a fresh token, so the iframe must not redirect itself
   * (that would load the portal login page inside the frame).
   */
  redirectToLogin() {
    if (isEmbedMode()) {
      return;
    }

    setTimeout(() => {
      try {
        store.dispatch('login/logout');
      } catch (error) {
        // Ignore — store may already be torn down.
      }
    }, 100);
  }

  /**
   * Clean up timers and mark the session inactive.
   */
  cleanup() {
    this.isActive = false;
    this.absoluteTimeoutWarningShown = false;

    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }
  }

  /**
   * Get session info for debugging.
   */
  getSessionInfo() {
    const sessionStartTime = sessionStorage.getItem(STORAGE_KEYS.SESSION_START);
    const expiry = this.getTokenExpiry();
    const refreshExpiry = this.getRefreshTokenExpiry();
    const { end } = this.getEffectiveSessionEnd();
    const now = Date.now();

    return {
      isActive: this.isActive,
      sessionStartTime: sessionStartTime ? new Date(parseInt(sessionStartTime, 10)).toISOString() : null,
      tokenExpiry: expiry ? new Date(expiry).toISOString() : null,
      timeUntilExpiryMinutes: expiry ? Math.round((expiry - now) / 1000 / 60) : null,
      hasRefreshToken: !!this.getRefreshToken(),
      refreshTokenExpiry: refreshExpiry ? new Date(refreshExpiry).toISOString() : null,
      timeUntilRefreshExpiryMinutes: refreshExpiry ? Math.round((refreshExpiry - now) / 1000 / 60) : null,
      effectiveSessionEnd: end ? new Date(end).toISOString() : null,
      timeUntilSessionEndMinutes: end ? Math.round((end - now) / 1000 / 60) : null,
    };
  }
}

// Singleton instance
const sessionManager = new SessionManager();

/**
 * On-demand access-token freshness check, intended to run in each service's
 * request interceptor BEFORE the token is read. Refreshes (once, shared) only
 * when the access token is within ACCESS_TOKEN_REFRESH_WINDOW of expiry and a
 * refresh token exists. Updates the store + sessionStorage, so whatever the
 * interceptor reads next is current. Never throws — a failed refresh lets the
 * request proceed with the existing token (the 401 path / session check own logout).
 *
 * @returns {Promise<void>}
 */
export async function ensureFreshToken() {
  debugLog('[ensureFreshToken] called');

  // No refresh capability (fallback mode) — nothing to ensure.
  if (!sessionManager.getRefreshToken()) {
    debugLog('[ensureFreshToken] skip: no refresh token (fallback mode)');
    return;
  }

  const expiry = sessionManager.getTokenExpiry();
  // Unknown expiry — assume the access token is usable.
  if (!expiry) {
    debugLog('[ensureFreshToken] skip: access-token expiry unknown');
    return;
  }

  const minutesLeft = Math.round((expiry - Date.now()) / 1000 / 60);

  // Still comfortably fresh — no refresh needed. (FORCE_REFRESH_ON_EVERY_CALL,
  // a test toggle, skips this short-circuit so every call refreshes.)
  if (!FORCE_REFRESH_ON_EVERY_CALL && expiry - Date.now() > ACCESS_TOKEN_REFRESH_WINDOW) {
    debugLog(`[ensureFreshToken] skip: access token still fresh (~${minutesLeft} min left)`);
    return;
  }

  // Backoff: after a recent failed refresh, don't re-attempt on every API call
  // (the forced refresh below bypasses refreshToken's own throttle). A refresh
  // already in flight is allowed through — callers just join it.
  if (!inFlightRefresh && (Date.now() - lastRefreshFailureAt) < REFRESH_FAILURE_COOLDOWN) {
    debugLog('[ensureFreshToken] skip: cooldown after a recent refresh failure');
    return;
  }

  debugLog(
    inFlightRefresh
      ? '[ensureFreshToken] joining in-flight refresh (another caller already triggered it)'
      : `[ensureFreshToken] refreshing access token (~${minutesLeft} min left)`
  );

  // Coalesce onto the process-wide single-flight refresh (shared with bootstrap
  // / init so there is never more than one refresh in flight).
  const result = await sessionManager.sharedRefresh();
  debugLog('[ensureFreshToken] refresh done', result);
}

/**
 * Mint a maximally-fresh access token to hand off to another platform (Provider Portal /
 * Unimod) on the outgoing `?jwtToken=` URL.
 *
 * Unlike ensureFreshToken(), which only refreshes inside ACCESS_TOKEN_REFRESH_WINDOW, this
 * FORCES a refresh regardless of remaining lifetime. A handoff is not a local request: the
 * receiving app validates the token before its very first API call, and between the click and
 * that call sit a full page load and the target's session bootstrap. A token that is "fresh
 * enough" to attach to a request here can easily land there already dead. Forcing the refresh
 * means the other platform always starts on a full token lifetime.
 *
 * The extra round trip is acceptable because switching platforms is a deliberate, infrequent
 * click rather than a per-request cost, and concurrent calls coalesce via sharedRefresh().
 *
 * Never throws, and never logs out: suppressLogout keeps a rejected refresh from tearing down
 * this session mid-click. If a refresh isn't possible or fails, the current token is returned
 * so the switch still proceeds — the target app owns the login redirect if it's unusable.
 *
 * @returns {Promise<string>} the token to put on the outgoing URL
 */
export async function getTokenForPlatformHandoff() {
  const readToken = () => store.getters['login/token'] || sessionStorage.getItem('token') || '';

  // Fallback mode (no refresh token was ever issued) — the access token is all we have.
  if (!sessionManager.getRefreshToken()) {
    debugLog('[getTokenForPlatformHandoff] no refresh token — handing off the current token');
    return readToken();
  }

  try {
    const result = await sessionManager.sharedRefresh({ suppressLogout: true });
    debugLog('[getTokenForPlatformHandoff] forced refresh done', result);
  } catch (e) {
    debugLog('[getTokenForPlatformHandoff] forced refresh failed — handing off current token');
  }

  return readToken();
}

export default sessionManager;
